/* PM and welcome kit guard (site-wide), updated 2026-10-06.
 * The LPs' claim flow adds the $0 kit (Shaker + $10 Gift Card, variant 43973444206710)
 * alongside the qualifying Peach Mango box (product 6567509885046). The LP-scoped
 * reconcile can't see cart edits made on OTHER pages, so an orphaned free kit could
 * survive box removal and be checked out alone. This guard runs everywhere:
 * if the kit is in the cart with NO qualifying product, remove it (absolute qty 0).
 * It never ADDS anything and never touches carts without kit variants.
 *
 * 2026-08-24: the GLP-1 welcome kit gives away this SAME $0 kit variant, so the guard
 * was deleting it ~400ms after cart-promo-gift-310 added it. QUALIFYING_PRODUCTS now
 * also lists the 7 All-In-One shakes that earn the GLP-1 kit. This does not loosen the
 * PM protection: a kit with no entitling product from EITHER campaign is still orphaned
 * and still removed. Entitlement itself (subscription-only) stays with
 * cart-promo-gift-310; this file remains a last-resort orphan sweeper.
 *
 * 2026-10-06: the three new $0 welcome kit variants require a qualifying All-In-One
 * subscription. Remove them without entitlement; otherwise keep at most one of each,
 * using line keys to remove duplicates regardless of gift properties. The old kit
 * rule stays unchanged. Both rules share one update request and drawer refresh.
 * Hydrate Tangerine is discount-controlled and is not guarded here.
 *
 * 2026-10-19 (Tim QA F03): the engine-added Hydrate Tangerine claim (variant
 * 44045321994358, carrying _promo_free_gift) stayed in the cart as a $34 PAID line
 * after the qualifying subscription was removed off the LP. It is now swept by the
 * same welcome entitlement, but ONLY lines carrying _promo_free_gift: a Hydrate the
 * shopper added themselves (no gift property, or the public variant) is never touched.
 * While a removal is pending the checkout buttons are locked, and cart-bus updates
 * reconcile immediately instead of after the 400ms drawer debounce.
 *
 * 2026-10-26: Recipe VIP gift frother must stay free and limited to one. Prefer the
 * _recipe_vip_gift line, remove duplicates, and remove the survivor when Shopify
 * reports a paid final_line_price. Shares the update, drawer refresh and checkout lock.
 *
 * 2026-10-30: the frother is now a $0 variant (it can't be charged if RECIPEVIP fails), so
 * the paid-price rule no longer removes an orphan. It is now kept only while the cart also
 * holds a product of type Shake, which matches RECIPEVIP's two qualifying collections.
 */
(function () {
  'use strict';
  var KIT_VARIANT = '43973444206710';
  var QUALIFYING_PRODUCTS = [
    '6567509885046',  // 310 Hydrate - Peach Mango (PM50 / PMB1G1)
    // GLP-1 welcome kit — the 7 All-In-One shakes whose SUBSCRIPTION earns the kit
    '6837909160054', '6880726483062', '6833351884918', '6966597517430',
    '8004391796854', '6862749073526', '6785245184118'
  ];
  var WELCOME_KIT_VARIANTS = ['48958114529398', '48958114594934', '48958114955382'];
  // Engine-added Hydrate Tangerine claim (welcome kit). Only lines with _promo_free_gift are swept.
  var WELCOME_HYDRATE_CLAIM = '44045321994358';
  var RECIPE_VIP_GIFT = '49153332117622';
  // Island gifts qualify with either purchase type of Tropical Coconut; the bottle requires subscription.
  var ISLAND_KIT_GIFTS = ['49078677602422', '49078677635190', '49078677700726', '49078677733494'];
  var ISLAND_BOTTLE = '49078677635190';
  var ISLAND_QUALIFIER = '39979933696118';
  // MUST match qualifyingProductIds in the glp1_kit_enabled block of snippets/promo-gift-config-310.liquid.
  // If a flavor qualifies there but not here, the LP adds the kit and this guard strips it, in a loop
  // (QA R2, 2026-10-06). The last 3 are Caramel Sundae, Horchata, Cookies N' Cream (added by PR #186/#187).
  var WELCOME_QUALIFYING_PRODUCTS = [
    '6643835764854', '6643836354678',
    '6837909160054', '6880726483062', '6833351884918', '6966597517430',
    '8004391796854', '6862749073526', '6785245184118',
    '6643836485750', '6860237963382', '6847175393398'
  ];
  var busy = false;
  var debounceT;
  // Tim QA F03: lock checkout the moment a cart that held a free gift changes, until reconcile confirms.
  var lastHadGift = false;
  var pending = false;
  var pendingSince = 0;

  function reconcile() {
    if (busy) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var kit = items.filter(function (i) { return String(i.variant_id) === KIT_VARIANT; })[0];
        var hasQualifying = items.some(function (i) {
          return QUALIFYING_PRODUCTS.indexOf(String(i.product_id)) !== -1;
        });
        var updates = {};
        if (kit && !hasQualifying) updates[KIT_VARIANT] = 0;
        var welcomeEntitled = items.some(function (i) {
          return i.selling_plan_allocation && WELCOME_QUALIFYING_PRODUCTS.indexOf(String(i.product_id)) !== -1;
        });
        WELCOME_KIT_VARIANTS.forEach(function (variantId) {
          var lines = items.filter(function (i) { return String(i.variant_id) === variantId; });
          // Prefer the auto-added line so the LP gift script does not re-add a removed duplicate.
          var survivor = lines.filter(function (i) { return i.properties && i.properties._promo_free_gift; })[0] || lines[0];
          lines.forEach(function (line) {
            var target = welcomeEntitled && line === survivor ? 1 : 0;
            if (line.quantity !== target) updates[line.key] = target;
          });
        });
        var hydrateGiftLines = items.filter(function (i) {
          return String(i.variant_id) === WELCOME_HYDRATE_CLAIM && i.properties && i.properties._promo_free_gift;
        });
        hydrateGiftLines.forEach(function (line, idx) {
          var target = welcomeEntitled && idx === 0 ? 1 : 0;
          if (line.quantity !== target) updates[line.key] = target;
        });
        var islandEntitled = items.some(function (i) { return String(i.variant_id) === ISLAND_QUALIFIER; });
        var islandSubscriptionEntitled = items.some(function (i) {
          return String(i.variant_id) === ISLAND_QUALIFIER && i.selling_plan_allocation;
        });
        var islandTargets = [];
        ISLAND_KIT_GIFTS.forEach(function (variantId) {
          var lines = items.filter(function (i) { return String(i.variant_id) === variantId; });
          var survivor = lines.filter(function (i) { return i.properties && i.properties._island_kit_gift; })[0] || lines[0];
          lines.forEach(function (line) {
            var target = islandEntitled && line === survivor ? 1 : 0;
            if (variantId === ISLAND_BOTTLE && !islandSubscriptionEntitled) target = 0;
            islandTargets.push({ line: line, variantId: variantId, target: target });
          });
        });
        // All-or-nothing: each Island discount needs its whole gift set, so a partial kit would be
        // charged at list. Subscription kit = 4 gifts; one-time kit = 3 (no bottle).
        var expectedGifts = ISLAND_KIT_GIFTS.filter(function (v) {
          return islandSubscriptionEntitled || v !== ISLAND_BOTTLE;
        });
        var keptGifts = expectedGifts.filter(function (v) {
          return islandTargets.some(function (t) { return t.variantId === v && t.target === 1; });
        });
        var partialKit = keptGifts.length > 0 && keptGifts.length < expectedGifts.length;
        islandTargets.forEach(function (t) {
          var target = partialKit ? 0 : t.target;
          if (t.line.quantity !== target) updates[t.line.key] = target;
        });
        var recipeGiftLines = items.filter(function (i) { return String(i.variant_id) === RECIPE_VIP_GIFT; });
        var recipeSurvivor = recipeGiftLines.filter(function (i) {
          return i.properties && i.properties._recipe_vip_gift;
        })[0] || recipeGiftLines[0];
        var recipeEntitled = items.some(function (i) {
          return i.product_type === 'Shake' && String(i.variant_id) !== RECIPE_VIP_GIFT;
        });
        recipeGiftLines.forEach(function (line) {
          var target = recipeEntitled && line === recipeSurvivor && line.final_line_price === 0 ? 1 : 0;
          if (line.quantity !== target) updates[line.key] = target;
        });
        lastHadGift = items.some(function (i) {
          var v = String(i.variant_id);
          return v === KIT_VARIANT || v === RECIPE_VIP_GIFT || WELCOME_KIT_VARIANTS.indexOf(v) !== -1 || ISLAND_KIT_GIFTS.indexOf(v) !== -1 ||
            (v === WELCOME_HYDRATE_CLAIM && i.properties && i.properties._promo_free_gift);
        });
        if (!Object.keys(updates).length) { setPending(false); lockCheckout(false); return; }
        busy = true;
        setPending(true);
        lockCheckout(true);
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: updates })
        })
          .then(function () {
            var cd = document.querySelector('cart-drawer');
            if (cd && typeof cd.refreshCartDrawer === 'function') return cd.refreshCartDrawer();
          })
          .catch(function () {})
          .finally(function () { busy = false; setPending(false); lockCheckout(false); });
      })
      .catch(function () { busy = false; setPending(false); lockCheckout(false); });
  }

  // Same pattern and label as CartPromoGift310.lockCheckout. Fail-open: always released in finally.
  function lockCheckout(disabled) {
    var btns = document.querySelectorAll('cart-drawer button[name="checkout"], form[action$="/cart"] button[name="checkout"]');
    Array.prototype.forEach.call(btns, function (b) {
      if (disabled) {
        if (b.dataset.kitGuardLabel) return;
        b.dataset.kitGuardLabel = b.textContent;
        b.dataset.kitGuardWasDisabled = b.hasAttribute('disabled') ? '1' : '0';
        b.setAttribute('disabled', 'true');
        b.textContent = 'Updating your free gift…';
      } else if (b.dataset.kitGuardLabel) {
        if (b.dataset.kitGuardWasDisabled !== '1') b.removeAttribute('disabled');
        b.textContent = b.dataset.kitGuardLabel;
        delete b.dataset.kitGuardLabel;
        delete b.dataset.kitGuardWasDisabled;
      }
    });
  }

  function setPending(on) { pending = on; pendingSince = on ? Date.now() : 0; }
  function holdIfGift() { if (lastHadGift) { setPending(true); lockCheckout(true); } }
  // Other cart scripts (e.g. the gift ladder) toggle the same checkout button's disabled state,
  // so the visual lock alone is not a guarantee. While a gift reconcile is pending, block the
  // checkout click/submit itself. Fail-open after 8s so a stuck request never traps a shopper.
  function checkoutBlocked() { return pending && (Date.now() - pendingSince) < 8000; }
  document.addEventListener('click', function (e) {
    if (!checkoutBlocked()) return;
    var t = e.target && e.target.closest && e.target.closest('button[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]');
    if (t) { e.preventDefault(); e.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('submit', function (e) {
    if (!checkoutBlocked()) return;
    var f = e.target, sub = e.submitter;
    if (f && f.matches && f.matches('form[action$="/cart"], form[action*="/checkout"]') &&
        (!sub || sub.name === 'checkout' || /checkout/.test(f.getAttribute('action') || ''))) {
      e.preventDefault(); e.stopImmediatePropagation();
    }
  }, true);
  function schedule() { holdIfGift(); clearTimeout(debounceT); debounceT = setTimeout(reconcile, 400); }
  function scheduleNow() { holdIfGift(); clearTimeout(debounceT); debounceT = setTimeout(reconcile, 0); }
  // Drawer re-renders replace the checkout button: re-apply a pending lock, and ignore the
  // mutations our own lock/unlock makes on that button (otherwise lock and unlock would loop).
  function onDrawerMutation(records) {
    var external = records.some(function (r) {
      var el = r.target && r.target.nodeType === 1 ? r.target : r.target && r.target.parentElement;
      return !(el && el.closest && el.closest('button[name="checkout"]'));
    });
    if (!external) return;
    if (checkoutBlocked()) lockCheckout(true);
    schedule();
  }

  function init() {
    // 1) theme cart bus (fires on quantity changes / removals done through theme UI)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleNow);
    }
    // 2) drawer re-renders (covers paths that bypass the bus)
    var cd = document.querySelector('cart-drawer');
    if (cd && 'MutationObserver' in window) {
      new MutationObserver(onDrawerMutation).observe(cd, { childList: true, subtree: true, characterData: true });
    }
    // 3) once on load, immediately (covers arriving on any page with an already-orphaned kit)
    scheduleNow();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
